import { SETUP_ROUTE_PERMISSIONS } from "./setupAccess";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import {
  ServerCog,
  UserCog,
  ClipboardCheck,
  Users,
  UserCircle,
  Calendar,
  Pill,
  CreditCard,
  Stethoscope,
  Settings,
  Activity,
  HeartPulse,
  Building2,
  AlertCircle,
  Package,
  TestTube,
  Landmark,
  ShieldCheck,
  ListChecks,
  HeartHandshake,
  Video,
  Radio,
  BarChart3,
  PillBottle,
} from "lucide-react";

export interface NavItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  route?: string;
  children?: NavItem[];
  /** Hide from navigation while keeping the page route available. */
  hidden?: boolean;
  /**
   * Shown only to users holding any of these (the same codes the screen's API
   * requires). Omitted = visible to everyone signed in. A group whose items are
   * all hidden is hidden too.
   */
  permissions?: string[];
}

/** Drops items the user cannot use, then groups left empty. */
function visibleItems(items: NavItem[], allowed: (codes: string[]) => boolean, filterByAccess = true): NavItem[] {
  const out: NavItem[] = [];
  for (const rawItem of items) {
    const item = { ...rawItem, permissions: (rawItem.route && SETUP_ROUTE_PERMISSIONS[rawItem.route]) || rawItem.permissions };
    if (filterByAccess && item.hidden) continue;
    if (filterByAccess && item.permissions?.length && !allowed(item.permissions)) continue;
    if (item.children) {
      const children = visibleItems(item.children, allowed, filterByAccess);
      if (!children.length && !item.route) continue;
      out.push({ ...item, children });
    } else {
      out.push(item);
    }
  }
  return out;
}

/**
 * The single source of truth for the app's module/route tree, shared by
 * LeftNav (the legacy drawer) and ErpShell (the persistent module rail).
 */
export function useNavItems(filterByAccess = true): NavItem[] {
  const { t } = useTranslation();
  const { isSuperAdmin, hasPermission, permissionsLoaded } = useAuth();

  return useMemo(
    () => visibleItems([
      {
        id: "dashboard",
        label: t("nav.dashboard") || "Dashboard",
        icon: Activity,
        route: "/dashboard",
      },

      {
        id: "reports",
        label: t("nav.reports", { defaultValue: "Reports" }),
        icon: BarChart3,
        route: "/reports",
        permissions: ["reports.management.view", "reports.operations.view", "reports.clinical.view", "reports.nursing.view", "reports.rcm.view", "reports.finance.view", "reports.diagnostics.view", "reports.inventory.view", "reports.hr.view", "reports.self.view", "reports.quality.view", "reports.compliance.view", "reports.assets.view", "reports.system.view"],
      },

      {
        id: "patients",
        label: t("nav.patients") || "Patients",
        icon: Users,
        children: [
          {
            id: "patients-list",
            label: t("nav.patientsList") || "All Patients",
            route: "/patients",
          },
          { id: "patient-visits", label: t("nav.visits", "Visits"), route: "/patients/visits" },
          {
            id: "patient-admissions",
            label: t("nav.patientAdmissions") || "Admissions",
            route: "/patients/admissions",
          },
          {
            id: "medical-records",
            label: t("nav.medicalRecords") || "Medical Records",
            route: "/patients/medical-records",
          },
          {
            id: "triage",
            label: t("nav.triage") || "Triage Data",
            route: "/patients/triage",
          },
        ],
      },
      {
        id: "emergency",
        label: t("nav.emergency") || "Emergency",
        icon: AlertCircle,
        children: [
          {
            id: "er-dashboard",
            label: t("nav.erDashboard") || "ER Dashboard",
            route: "/er/dashboard",
          },
          {
            id: "er-visits",
            label: t("nav.erVisits") || "ER Visits",
            route: "/er/visits",
          },
          {
            id: "er-triage",
            label: t("nav.erTriage") || "ER Triage",
            route: "/er/triage",
          },
          {
            id: "er-triage-queue",
            label: t("nav.erTriageQueue") || "Triage Queue",
            route: "/er/triage-queue",
          },
          {
            id: "er-beds",
            label: t("nav.erBedManagement") || "Bed Management",
            route: "/er/beds",
          },
        ],
      },
      {
        id: "nursing",
        label: t("nav.nursing") || "Nursing (IPD)",
        icon: HeartPulse,
        children: [
          {
            id: "nursing-station",
            label: t("nav.nursingStation") || "Nursing Station",
            route: "/nursing/station",
            permissions: ["ipd.vitals.view", "nursing.handover.record", "nursing.assignments.manage"],
          },
          {
            id: "nursing-dialysis",
            hidden: true,
            label: t("nav.dialysisStation", { defaultValue: "Dialysis Station" }),
            route: "/nursing/dialysis",
            permissions: ["sessions.view"],
          },
          {
            id: "nursing-sessions",
            label: t("nav.clinicalSessions", { defaultValue: "Clinical Sessions" }),
            route: "/nursing/sessions",
            permissions: ["sessions.view"],
          },
          {
            id: "nursing-allocation",
            label: t("nav.clinicalRoster", { defaultValue: "Nursing & Clinical Roster" }),
            route: "/hrms/duty-roster",
          },
          {
            id: "nursing-patient-assignments",
            label: t("nav.patientAssignments", { defaultValue: "Patient Assignments" }),
            route: "/nursing/allocation",
            permissions: ["nursing.assignments.manage"],
          },
          {
            id: "ipd-orders",
            label: t("nav.ipdOrders") || "Inpatient Orders",
            route: "/ipd/orders",
            permissions: ["ipd.order.view"],
          },
          {
            id: "ipd-investigations",
            label: t("nav.wardInvestigations") || "Ward Investigations",
            route: "/ipd/investigations",
            permissions: ["ipd.order.view"],
          },
          {
            id: "ipd-kitchen",
            label: t("nav.ipdKitchen") || "Kitchen Worklist",
            route: "/ipd/kitchen",
            permissions: ["ipd.diet.view"],
          },
          {
            id: "ipd-billing",
            label: t("nav.ipdBilling") || "IPD Billing",
            route: "/ipd/billing",
            permissions: ["ipd.billing.view"],
          },
          {
            id: "ipd-payer",
            label: t("nav.ipdPayer") || "Payer / TPA",
            route: "/ipd/payer",
          },
          {
            id: "tpa-master",
            label: t("nav.tpaMaster") || "TPA Master",
            route: "/ipd/tpa-master",
          },
          {
            id: "counselor-policies",
            label: "Counselor Policies",
            route: "/ipd/counselor-policies",
          },
          {
            id: "counselor-quotes",
            label: "Counselor Quotations",
            route: "/counselor/quotes",
          },
          {
            id: "ipd-ot-consumables",
            label: t("nav.ipdOtConsumables") || "OT Consumables",
            route: "/ipd/ot-consumables",
          },
          {
            id: "ipd-claims",
            label: t("nav.ipdClaims") || "Insurance Claims",
            route: "/ipd/claims",
          },
          {
            id: "procedure-packages",
            label: "Procedure Packages",
            route: "/ipd/procedure-packages",
          },
          {
            id: "package-reconciliation",
            label: "Package Reconciliation",
            route: "/ipd/package-reconciliation",
          },
          {
            id: "preference-card-templates",
            label: "Consumable Kits & Preference Cards",
            route: "/ipd/preference-cards",
          },
        ],
      },
      {
        id: "communications",
        label: "Communications",
        icon: Radio,
        children: [
          { id: "staff-paging", label: "Staff Paging", route: "/communications/staff-paging", permissions: ["staff_page.create", "staff_page.receive"] },
        ],
      },
      {
        id: "doctors",
        label: t("nav.doctors") || "Doctors",
        icon: Stethoscope,
        children: [
          {
            id: "ipd-doctor-dashboard",
            label: t("nav.ipdDoctorDashboard") || "Doctor Dashboard",
            route: "/ipd/doctor-dashboard",
          },
          {
            id: "doctors-list",
            label: t("nav.doctorsList") || "All Doctors",
            route: "/doctors",
          },
          {
            id: "doctor-slots",
            label: t("nav.doctorSlots") || "Time Slots",
            route: "/doctors/slots",
          },
        ],
      },
      {
        id: "rooms",
        label: t("nav.roomsOperations") || "Room Operations",
        icon: Building2,
        children: [
          {
            id: "rooms-inventory",
            label: t("nav.roomInventory") || "Room Inventory",
            route: "/rooms/inventory",
          },
          {
            id: "rooms-occupancy",
            label: t("nav.roomOccupancy") || "Occupancy Board",
            route: "/rooms/board",
          },
          {
            id: "procedure-bookings",
            label: t("nav.procedureBookings") || "OT & Procedure Bookings",
            route: "/rooms/procedure-bookings",
          },
          {
            id: "daycare",
            label: t("nav.daycare") || "Day-care Management",
            route: "/rooms/daycare",
          },
          {
            id: "housekeeping",
            label: t("nav.housekeeping") || "Housekeeping",
            route: "/rooms/housekeeping",
          },
          {
            id: "block-bookings",
            label: t("nav.blockBookings") || "Block Bookings",
            route: "/rooms/block-bookings",
          },
          {
            id: "maintenance",
            label: t("nav.maintenance") || "Maintenance",
            route: "/rooms/maintenance",
          },
          {
            id: "room-pricing",
            label: t("nav.roomPricing") || "Pricing Packages",
            route: "/rooms/pricing",
          },
          {
            id: "room-billing",
            label: t("nav.roomBilling") || "Room Billing",
            route: "/rooms/billing",
          },
          {
            id: "room-analytics",
            label: t("nav.roomAnalytics") || "Room Analytics",
            route: "/rooms/analytics",
          },
        ],
      },
      {
        id: "appointments",
        label: t("nav.appointments") || "Appointments",
        icon: Calendar,
        children: [
          {
            id: "donna-conversations",
            label: t("nav.patientConversations", { defaultValue: "Patient Conversations" }),
            route: "/donna/conversations",
            permissions: ["donna.conversations.manage"],
          },
          {
            id: "appointments-heatmap",
            label: t("nav.opdWorkloadHeatMap", { defaultValue: "OPD Workload Heat Map" }),
            route: "/appointments/heatmap",
          },
          {
            id: "appointments-list",
            label: t("nav.appointmentsList") || "All Appointments",
            route: "/appointments",
          },
          {
            id: "opd-schedule",
            label: t("nav.opdSchedule") || "OPD Schedule",
            route: "/appointments/opd-schedule",
            children: [
              {
                id: "opd-schedule-view",
                label: t("nav.opdScheduleView") || "Schedule View",
                route: "/appointments/opd-schedule",
              },
              {
                id: "opd-conflicts",
                label: t("nav.opdConflicts") || "Conflicts & Rescheduling",
                route: "/appointments/opd-schedule/conflicts",
              },
              {
                id: "opd-smart-scheduler",
                label: t("nav.opdSmartScheduler") || "Smart Scheduler",
                route: "/appointments/opd-schedule/smart-scheduler",
              },
              {
                id: "opd-room-assignment",
                label: t("nav.opdRoomAssignment") || "Room Assignment",
                route: "/appointments/opd-schedule/rooms",
              },
              {
                id: "opd-availability",
                label: t("nav.opdAvailability") || "Doctor Availability",
                route: "/appointments/opd-schedule/availability",
              },
              {
                id: "doctor-room-assignments",
                label: t("nav.doctorRoomAssignments") || "Doctor Room Assignments",
                route: "/appointments/opd-schedule/doctor-room-assignments",
              },
            ],
          },
          {
            id: "appointment-calendar",
            label: t("nav.appointmentCalendar") || "Calendar View",
            route: "/appointments/calendar",
          },
          {
            // The public page needs this hospital's booking code (?h=...); the
            // link to share, with a copy button, is on Public Links & Keys.
            id: "public-booking",
            label: t("nav.onlineBookingLink", { defaultValue: "Online Booking Link" }),
            route: "/settings/public-access",
            permissions: ["settings.public_access.manage"],
          },
          {
            id: "telemedicine",
            label: t("nav.telemedicine") || "Telemedicine",
            icon: Video,
            route: "/appointments/telemedicine",
          },
        ],
      },
      {
        id: "prescriptions",
        label: t("nav.prescriptions") || "Prescriptions",
        icon: Pill,
        children: [
          {
            id: "prescriptions-list",
            label: t("nav.prescriptionsList") || "All Prescriptions",
            route: "/prescriptions",
          },
          {
            id: "prescription-details",
            label: t("nav.prescriptionDetails") || "Prescription Details",
            route: "/prescriptions/details",
          },
          {
            id: "medications",
            label: t("nav.medications") || "Medications",
            route: "/prescriptions/medications",
          },
        ],
      },
      {
        id: "billing",
        label: t("nav.billing") || "Billing & Payments",
        icon: CreditCard,
        children: [
          {
            id: "billing-worklist",
            label: t("nav.billingWorklist", { defaultValue: "Patients" }),
            route: "/billing",
          },
          {
            id: "unbilled-ageing",
            label: t("nav.unbilledAgeing", { defaultValue: "Unbilled Charges" }),
            route: "/billing/unbilled",
          },
          {
            id: "billing-list",
            label: t("nav.billingList", { defaultValue: "Bills" }),
            route: "/billing/bills",
          },
          {
            id: "pending-gst-submission",
            label: t("nav.pendingGstSubmission", { defaultValue: "Pending GST Submission" }),
            route: "/billing/gst-pending",
          },
          {
            id: "billed-items",
            label: t("nav.billedItems") || "Billed Items",
            route: "/billing/items",
          },
          {
            id: "payments",
            label: t("nav.payments") || "Payments",
            route: "/billing/payments",
          },
          {
            id: "pos-queue",
            label: "POS & Online Queue",
            route: "/billing/pos-queue",
          },
          {
            id: "discounts",
            label: t("nav.discounts") || "Discounts",
            route: "/billing/discounts",
          },
        ],
      },
      {
        id: "lab-tests",
        label: t("nav.labTests") || "Lab Tests",
        icon: TestTube,
        children: [
          {
            id: "lab-queue",
            label: t("nav.labQueue") || "Investigation & Lab Queue",
            route: "/lab/queue",
          },
          {
            id: "lab-fulfillment",
            label: t("nav.labFulfillment") || "Lab Fulfillment (outpatient)",
            route: "/lab-fulfillment",
          },
        ],
      },
      {
        id: "inventory",
        label: t("nav.inventory") || "Inventory",
        icon: Package,
        children: [
          {
            id: "inventory-dashboard",
            label: t("nav.inventoryDashboard") || "Dashboard",
            route: "/inventory/dashboard",
          },
          {
            id: "inventory-items",
            label: t("nav.inventoryItems") || "Item Master",
            route: "/inventory/items",
          },
          {
            id: "inventory-prescription-categories",
            label: t("nav.inventoryPrescriptionCategories", { defaultValue: "Prescription Categories" }),
            route: "/inventory/prescription-categories",
          },
          {
            id: "inventory-stores",
            label: t("nav.inventoryStores", { defaultValue: "Stores" }),
            route: "/inventory/stores",
          },
          {
            id: "inventory-stock",
            label: t("nav.inventoryStock") || "Stock Ledger",
            route: "/inventory/stock",
          },
          {
            id: "inventory-purchase-orders",
            label: t("nav.inventoryPurchaseOrders") || "Purchase Orders",
            route: "/inventory/purchase-orders",
          },
          {
            id: "inventory-grn",
            label: t("nav.inventoryGrn") || "Goods Receipt (GRN)",
            route: "/inventory/grn",
          },
          {
            id: "inventory-issues",
            label: t("nav.inventoryIssues") || "Issue Requests",
            route: "/inventory/issues",
          },
          {
            id: "inventory-returns",
            label: t("nav.inventoryReturns") || "Return Requests",
            route: "/inventory/returns",
          },
          {
            id: "inventory-assets",
            label: "Assets & Depreciation",
            route: "/inventory/assets",
            permissions: ["inventory.assets.view"],
          },
          {
            id: "inventory-serials",
            label: t("nav.inventorySerials") || "Stock Serials",
            route: "/inventory/serials",
          },
          {
            id: "inventory-consignment",
            label: t("nav.inventoryConsignment") || "Consignment Reconciliation",
            route: "/inventory/consignment",
          },
          {
            id: "inventory-par-levels",
            label: t("nav.inventoryParLevels") || "Par Levels",
            route: "/inventory/par-levels",
          },
          {
            id: "inventory-replenishment",
            label: t("nav.inventoryReplenishment") || "Replenishment Worklist",
            route: "/inventory/replenishment",
          },
          {
            id: "inventory-recalls",
            label: t("nav.inventoryRecalls") || "Recalls & Quarantine",
            route: "/inventory/recalls",
          },
          {
            id: "inventory-adjustments",
            label: t("nav.inventoryAdjustments") || "Stock Adjustments",
            route: "/inventory/adjustments",
          },
          {
            id: "inventory-reorder",
            label: t("nav.inventoryReorder") || "Reorder & Alerts",
            route: "/inventory/reorder",
          },
          {
            id: "inventory-vendors",
            label: t("nav.inventoryVendors") || "Vendors",
            route: "/inventory/vendors",
          },
          {
            // Shortcut: the full payables set lives under Accounting > Payables.
            id: "inventory-vendor-invoices",
            label: t("nav.vendorInvoices", { defaultValue: "Vendor Invoices" }),
            route: "/settings/vendor-invoices",
            permissions: ["accounting.vendorinvoice.view"],
          },
          {
            id: "inventory-reports",
            label: t("nav.inventoryReports") || "Reports & Analytics",
            route: "/inventory/reports",
          },
        ],
      },
      {
        id: "medicines",
        label: t("nav.medicines") || "Pharmacy",
        icon: PillBottle,
        children: [
          {
            id: "medicines-list",
            label: t("nav.medicinesList") || "Medicine Master",
            route: "/medicines",
          },
          {
            id: "medicine-purchases",
            label: t("nav.medicinePurchases") || "Purchases",
            route: "/medicines/purchases",
          },
          {
            id: "medicine-sales",
            label: t("nav.medicineSales") || "Sales",
            route: "/medicines/sales",
          },
          {
            id: "pharmacy-fulfillment",
            label: t("nav.pharmacyFulfillment") || "Pharmacy Fulfillment",
            route: "/pharmacy-fulfillment",
          },
        ],
      },
      {
        id: "staff",
        label: t("nav.staff") || "HRMS",
        icon: UserCircle,
        children: [
          {
            id: "hrms-dashboard",
            label: t("nav.hrmsDashboard") || "HRMS Dashboard",
            route: "/hrms/dashboard",
          },
          {
            id: "hrms-employees",
            label: t("nav.hrmsEmployees") || "Employees",
            route: "/hrms/employees",
          },
          {
            id: "hrms-attendance",
            label: t("nav.hrmsAttendance") || "Attendance",
            route: "/hrms/attendance",
          },
          {
            id: "hrms-shifts",
            label: t("nav.hrmsShifts") || "Shift Management",
            route: "/hrms/shifts",
          },
          {
            id: "hrms-leave",
            label: t("nav.hrmsLeave") || "Leave Management",
            route: "/hrms/leave",
          },
          {
            id: "hrms-payroll",
            label: t("nav.hrmsPayroll", { defaultValue: "Payroll" }),
            children: [
              { id: "payroll-runs", label: t("nav.payrollRuns", { defaultValue: "Payroll Runs" }), route: "/hrms/payroll?tab=runs", permissions: ["hrms.payroll.view"] },
              { id: "payroll-salaries", label: t("nav.payrollSalaries", { defaultValue: "Salaries" }), route: "/hrms/payroll?tab=salaries", permissions: ["hrms.payroll.view"] },
              { id: "payroll-contracts", label: t("nav.payrollContracts", { defaultValue: "Contracts" }), route: "/hrms/payroll?tab=contracts", permissions: ["hrms.payroll.view"] },
              { id: "payroll-structures", label: t("nav.payrollStructures", { defaultValue: "Salary Structures & Components" }), route: "/hrms/payroll?tab=structures", permissions: ["hrms.payroll.view"] },
              { id: "payroll-shift-allowances", label: t("nav.payrollShiftAllowances", { defaultValue: "Shift Allowances" }), route: "/hrms/payroll?tab=shift-allowances", permissions: ["hrms.payroll.view"] },
              { id: "payroll-settings", label: t("nav.payrollSettings", { defaultValue: "Payroll Settings" }), route: "/hrms/payroll?tab=settings", permissions: ["hrms.payroll.view"] },
            ],
          },
          {
            id: "hrms-recruitment",
            label: t("nav.hrmsRecruitment") || "Recruitment",
            route: "/hrms/recruitment",
          },
          {
            id: "hrms-performance",
            label: t("nav.hrmsPerformance") || "Performance",
            route: "/hrms/performance",
          },
          {
            id: "hrms-training",
            label: t("nav.hrmsTraining") || "Training",
            route: "/hrms/training",
          },
          {
            id: "hrms-duty-roster",
            label: t("nav.hrmsDutyRoster") || "Duty Roster",
            route: "/hrms/duty-roster",
          },
          {
            id: "hrms-healthcare-extensions",
            label: t("nav.hrmsHealthcareExtensions") || "Healthcare Extensions",
            route: "/hrms/healthcare-extensions",
          },
          {
            id: "hrms-settings",
            label: t("nav.hrmsSettings") || "HR Settings",
            route: "/hrms/settings",
          },
        ],
      },
      {
        id: "accounting",
        label: t("nav.accounting", { defaultValue: "Accounting" }),
        icon: Landmark,
        children: [
          {
            id: "accounting-setup",
            label: t("nav.accountingSetup", { defaultValue: "Setup" }),
            children: [
              { id: "chart-of-accounts", label: t("nav.chartOfAccounts", { defaultValue: "Chart of Accounts" }), route: "/settings/chart-of-accounts", permissions: ["accounting.ledger.view", "accounting.coa.manage"] },
              { id: "posting-accounts", label: t("nav.postingAccounts", { defaultValue: "Posting Accounts" }), route: "/settings/posting-accounts", permissions: ["accounting.ledger.view", "accounting.coa.manage"] },
              { id: "fiscal-years", label: t("nav.fiscalYears", { defaultValue: "Fiscal Years" }), route: "/settings/fiscal-years", permissions: ["accounting.ledger.view", "accounting.period.manage", "accounting.period.close", "accounting.year.close"] },
              { id: "department-cost-centers", label: t("nav.departmentCostCenters", { defaultValue: "Departments & Cost Centers" }), route: "/settings/department-cost-centers", permissions: ["accounting.ledger.view", "accounting.coa.manage"] },
              { id: "collection-routing", label: t("nav.collectionRouting", { defaultValue: "Banks & Collection Routing" }), route: "/settings/collection-routing", permissions: ["accounting.routing.view", "accounting.routing.manage"] },
            ],
          },
          {
            id: "accounting-daily",
            label: t("nav.accountingDaily", { defaultValue: "Daily Work" }),
            children: [
              { id: "journal-entries", label: t("nav.journalEntries", { defaultValue: "Journal Entries" }), route: "/settings/journal-entries", permissions: ["accounting.ledger.view"] },
              { id: "manual-journals", label: t("nav.manualJournals", { defaultValue: "Manual Journals" }), route: "/settings/manual-journals", permissions: ["accounting.journal.view", "accounting.journal.create", "accounting.journal.approve"] },
              { id: "bank-reconciliation", label: t("nav.bankReconciliation", { defaultValue: "Bank Reconciliation" }), route: "/settings/bank-reconciliation", permissions: ["accounting.bankrec.view"] },
              { id: "credit-notes", label: t("nav.creditNotes", { defaultValue: "Credit Notes" }), route: "/settings/credit-notes", permissions: ["accounting.creditnote.view"] },
              { id: "doctor-commissions", label: t("nav.doctorCommissions", { defaultValue: "Doctor Commissions" }), route: "/settings/doctor-commissions", permissions: ["accounting.voucher.view"] },
            ],
          },
          {
            id: "accounting-payables",
            label: t("nav.accountingPayables", { defaultValue: "Payables" }),
            children: [
              { id: "vendor-invoices", label: t("nav.vendorInvoices", { defaultValue: "Vendor Invoices" }), route: "/settings/vendor-invoices", permissions: ["accounting.vendorinvoice.view"] },
              { id: "vendor-payments", label: t("nav.vendorPayments", { defaultValue: "Vendor Payments & Credits" }), route: "/settings/vendor-payments", permissions: ["accounting.vendorpayment.view"] },
              { id: "vendor-credit-notes", label: t("nav.vendorCreditNotes", { defaultValue: "Vendor Credit Notes" }), route: "/settings/vendor-credit-notes", permissions: ["accounting.vendorcreditnote.view"] },
              { id: "vendor-expenses", label: t("nav.vendorExpenses", { defaultValue: "Vendor Expenses" }), route: "/settings/vendor-expenses", permissions: ["accounting.vendorexpense.view"] },
              { id: "ap-ageing", label: t("nav.apAgeing", { defaultValue: "AP Ageing" }), route: "/settings/ap-ageing", permissions: ["accounting.vendorinvoice.view"] },
              { id: "procurement-chain", label: t("nav.procurementChain", { defaultValue: "Procurement Chain" }), route: "/settings/procurement-chain", permissions: ["accounting.vendorinvoice.view"] },
            ],
          },
          {
            id: "accounting-reports",
            label: t("nav.accountingReports", { defaultValue: "Reports" }),
            children: [
              { id: "financial-statements", label: t("nav.financialStatements", { defaultValue: "Financial Statements" }), route: "/settings/financial-statements", permissions: ["accounting.ledger.view"] },
              { id: "general-ledger", label: t("nav.generalLedger", { defaultValue: "General Ledger" }), route: "/settings/general-ledger", permissions: ["accounting.ledger.view"] },
              { id: "gst-returns", label: t("nav.gstReturns", { defaultValue: "GST Returns" }), route: "/settings/gst-returns", permissions: ["accounting.gst.view"] },
            ],
          },
        ],
      },
      {
        // Staff logins and what they may do; shown only to people who manage them.
        id: "users-access",
        label: t("nav.usersAccess", { defaultValue: "Users & Access" }),
        icon: UserCog,
        children: [
          { id: "admin-staff-users", label: t("nav.staffUsers", { defaultValue: "Staff Users" }), route: "/admin/users", permissions: ["USER_MANAGEMENT"] },
          { id: "admin-staff-roles", label: t("nav.rolesPermissions", { defaultValue: "Roles & Permissions" }), route: "/admin/roles", permissions: ["ROLE_MANAGEMENT", "USER_MANAGEMENT"] },
        ],
      },
      {
        // Master data a hospital sets up once (was spread over Administration and Onboarding).
        id: "hospital-setup",
        label: t("nav.hospitalSetup", { defaultValue: "Hospital Setup" }),
        icon: ClipboardCheck,
        children: [
          { id: "go-live-checklist", label: t("nav.goLiveChecklist", { defaultValue: "Go-live Checklist" }), route: "/setup/checklist", permissions: ["USER_MANAGEMENT"] },
          { id: "admin-departments", label: t("nav.hrmsDepartments", { defaultValue: "Departments & Designations" }), route: "/hrms/departments" },
          { id: "admin-cost-centers", label: t("nav.hrmsCostCenters", { defaultValue: "Cost Centers" }), route: "/hrms/cost-centers" },
          { id: "admin-wards-units", label: t("nav.nursingUnits", { defaultValue: "Wards & Nursing Units" }), route: "/nursing/units" },
          { id: "rooms-setup", label: t("nav.onboardingRoomsSetup", { defaultValue: "Buildings, Floors & Rooms" }), route: "/rooms/settings" },
          { id: "admin-admission-masters", label: t("nav.admissionMasters", { defaultValue: "Admission Masters" }), route: "/patients/admission-masters" },
          { id: "admin-service-catalog", label: t("nav.serviceCatalog", { defaultValue: "Service Catalog & Tariff" }), route: "/settings/service-catalog" },
          { id: "er-packages", label: t("er.packages.title", "ER packages"), route: "/settings/er-packages", permissions: ["er.packages.manage"] },
          { id: "admin-session-types", label: t("nav.sessionTypes", { defaultValue: "Session Types" }), route: "/settings/session-types", permissions: ["sessions.types.manage"] },
          { id: "admin-consultation-fees", label: t("nav.consultationFees", { defaultValue: "Consultation Fees" }), route: "/billing/consultation-fees" },
          { id: "admin-hsn-reference", label: t("nav.inventoryHsnReference", { defaultValue: "HSN Reference Codes" }), route: "/inventory/hsn-reference" },
          { id: "admin-item-code-config", label: t("nav.inventoryItemCodeConfig", { defaultValue: "Item Code Config" }), route: "/inventory/item-code-config" },
        ],
      },
      {
        id: "compliance",
        label: t("nav.compliance", { defaultValue: "Compliance" }),
        icon: ShieldCheck,
        children: [
          { id: "admin-licensing", label: t("nav.hrmsLicensing", { defaultValue: "Licensing & Certifications" }), route: "/hrms/licensing" },
          { id: "admin-compliance-regulations", label: t("nav.complianceRegulations", { defaultValue: "Regulations & Laws" }), route: "/compliance/regulations" },
          { id: "admin-compliance-documents", label: t("nav.complianceDocuments", { defaultValue: "Document Repository" }), route: "/compliance/documents" },
          { id: "admin-compliance-main", label: t("nav.complianceAudits", { defaultValue: "Statutory Audits & Records" }), route: "/compliance" },
        ],
      },
      {
        id: "quality",
        label: t("nav.quality", { defaultValue: "Quality" }),
        icon: ListChecks,
        children: [
          { id: "quality-worklists", label: t("nav.qualityWorklists", { defaultValue: "Worklists" }), route: "/quality/worklists", permissions: ["quality.worklist.view"] },
          { id: "ai-evaluation", label: t("nav.aiEvaluation", { defaultValue: "AI Evaluation" }), route: "/settings/ai/evaluation", permissions: ["ai.evaluation.review"] },
        ],
      },
      {
        id: "settings",
        label: t("nav.settings", { defaultValue: "Settings" }),
        icon: Settings,
        children: [
          { id: "general-settings", label: t("nav.generalSettings", { defaultValue: "General Settings" }), route: "/settings" },
          { id: "tax-setup", label: t("nav.taxSetup", { defaultValue: "Tax Setup" }), route: "/settings/taxes" },
          { id: "business-locations", label: t("nav.businessLocations", { defaultValue: "Business Locations" }), route: "/settings/business-locations" },
          { id: "label-templates", label: t("nav.labelTemplates", { defaultValue: "Label Templates" }), route: "/settings/label-templates", permissions: ["settings.label_templates.view", "settings.label_templates.manage"] },
          { id: "document-numbering", label: t("nav.documentNumbering", { defaultValue: "Document Numbering" }), route: "/settings/document-numbering", permissions: ["settings.numbering.manage"] },
          { id: "emergency-codes", label: t("nav.emergencyCodes", { defaultValue: "Emergency Codes" }), route: "/settings/emergency-codes" },
          { id: "report-translations", label: t("nav.reportTranslations", { defaultValue: "Report Translations" }), route: "/settings/translations" },
          {
            id: "settings-integrations",
            label: t("nav.integrationsGroup", { defaultValue: "Integrations" }),
            children: [
              { id: "integrations", label: t("nav.integrations", { defaultValue: "WhatsApp, Email, SMS & Zoom" }), route: "/settings/integrations", permissions: ["settings.integrations.manage"] },
              { id: "ai-settings", label: t("nav.aiIntegration", { defaultValue: "AI" }), route: "/settings/ai", permissions: ["settings.ai.manage"] },
              { id: "ask-donna", label: t("nav.askDonna", { defaultValue: "Ask Donna" }), route: "/settings/ai/donna", permissions: ["ai.evaluation.review", "settings.ai.manage"] },
              { id: "payment-gateways", label: t("nav.paymentGateways", { defaultValue: "Payment Gateways" }), route: "/admin/payment-gateways", permissions: ["billing.pos.manage"] },
              { id: "public-access", label: t("nav.publicAccess", { defaultValue: "Public Links & Keys" }), route: "/settings/public-access", permissions: ["settings.public_access.manage"] },
              { id: "pacs-settings", label: t("nav.pacsSettings", { defaultValue: "PACS / Imaging" }), route: "/settings/pacs", permissions: ["settings.public_access.manage"] },
            ],
          },
        ],
      },
      {
        id: "community",
        label: "Community Care",
        icon: HeartHandshake,
        children: [
          {
            id: "community-care",
            label: "Community Fund & Cases",
            route: "/community/care",
          },
        ],
      },
      // Across all hospitals: super admins only (the API refuses everyone else too).
      ...(isSuperAdmin
        ? [{
            id: "platform-console",
            label: t("nav.platformConsole", { defaultValue: "Platform Console" }),
            icon: ServerCog,
            route: "/platform",
          }]
        : []),
    ],
      // Until permissions load, gated items stay hidden rather than flash in and out.
      (codes) => isSuperAdmin || (!!permissionsLoaded && codes.some((code) => hasPermission(code))),
      filterByAccess,
    ),
    [t, isSuperAdmin, permissionsLoaded, hasPermission, filterByAccess]
  );
}
